import React, {useState} from "react"
import {
    Alert,
    Button,
    Grid,
    Link,
    Stack,
    Typography,
    Card,
    CardHeader,
    CardContent,
    Avatar,
    Tooltip,
    IconButton
} from '@mui/material';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import useApiCall, {makeApiCall} from "../../hooks/CancellableApiCall";
import {CareersResponse} from "../../data/CareersResponse";
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import useUser from "../../hooks/useUser";
import {LoadingContent} from "../../components/LoadingContent";
import {Division} from "../../data/Division";
import {NewApplicationDivisionSelectionForm} from "./NewApplicationDivisionSelectionForm";
import {Link as RouterLink, useNavigate} from "react-router-dom";
import {ApplicationRequirements} from "../../data/applications/ApplicationRequirements";
import {divisionIdentifierToDisplayName, divisionIdentifierUrlPrettifier} from "../../utils";
import {ConfirmationDialog} from "../../components/ConfirmationDialog";
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';

interface applicationRequirementProps {
    type: string,
    param: string,
    met: boolean | undefined
}

export const ApplicationRequirement = (props: applicationRequirementProps) => {
    let requirement
    if (props.met) {
        requirement = <Stack spacing={1} direction="row" alignItems="center">
            <CheckCircleOutlinedIcon color="success" fontSize="small"/>
            <Typography variant="body2"> {ApplicationRequirements.get(props.type)?.displayName}: {props.param}</Typography>
        </Stack>
    } else if (!props.met) {
        requirement = <Stack spacing={1} direction="row" alignItems="center">
            <ErrorOutlineOutlinedIcon color="error" fontSize="small"/>
            <Typography variant="body2"> {ApplicationRequirements.get(props.type)?.displayName}: {props.param}</Typography>
        </Stack>
    } else {
        requirement = <Stack spacing={1} direction="row" alignItems="center">
            <QuestionMarkIcon color="warning" fontSize="small"/>
            <Typography variant="body2"> {ApplicationRequirements.get(props.type)?.displayName}: {props.param}</Typography>
        </Stack>
    }

    return requirement
}

const Application = (props: CareersResponse) => {
    const [applyConfirm, setApplyConfirm] = useState(false)
    const [withdrawConfirm, setWithdrawConfirm] = useState(false)
    const navigate = useNavigate()
    const userData = useUser()
    const user = userData.user
    let applicationBanner = <></>;
    let applicationButton = <></>;
    let lastAssessmentButton = <></>
    let managementButtons = <></>
    let meetsRequirements = true

    const apply = (confirm: boolean) => {
        if (confirm) {
            makeApiCall({
                url: `/api/careers/${props.id}/apply`,
                method: "POST",
                body: {},
                onLoadedCallback: () => {
                    navigate(`/assessments/user/${props.id}/take`)
                },
                onError: () => {}
            })
        }
        setApplyConfirm(false)
    }

    const withdraw = (confirm: boolean) => {
        if (confirm) {
            makeApiCall({
                url: `/api/careers/${props.currentAssessment}/withdraw`,
                method: "POST",
                body: {},
                onLoadedCallback: () => {
                    navigate(`/login`)
                },
                onError: () => {}
            })
        }
        setWithdrawConfirm(false)
    }

    props.requirements?.forEach(r => {
        if (!r.met) {
            meetsRequirements = false
        }
    })

    let editApplicationRequirementsButton = <></>
    if (props.canEdit) {
        editApplicationRequirementsButton = <IconButton
            sx={{float: "right"}}
            size="small"
            color="info"
            component={RouterLink}
            to={`/edit-application-requirements/${props.type.replaceAll("_", "-")}/${props.divisionIdentifier}/${props.id}`}
        >
            <EditOutlinedIcon/>
        </IconButton>
    }

    if (props.canEdit) {
        let applicationStateButton = <></>
        if (props.state === "open") {
            applicationStateButton =
            <Tooltip title="Lock Application">
            <IconButton sx={{float: "right"}} size="small" color="warning" onClick={() => {
                   makeApiCall({
                       url: `/api/careers/${props.id}/state`,
                       method: "PATCH",
                       body: {
                           state: "locked"
                       },
                       onLoadedCallback: () => {
                           navigate(`/careers/overview/${props.id}/passed`)
                       },
                       onError: () => {}
                   })
               }}> <LockOutlinedIcon/> </IconButton>
            </Tooltip>
        } else if (props.state === "locked") {
            applicationStateButton =
            <Tooltip title="Open Application">
            <IconButton sx={{float: "right"}} size="small" color="success" onClick={() => {
                   makeApiCall({
                       url: `/api/careers/${props.id}/state`,
                       method: "PATCH",
                       body: {
                           state: "open"
                       },
                       onLoadedCallback: () => {
                           navigate(`/careers/overview/${props.id}/passed`)
                       },
                       onError: () => {}
                   })
               }}> <LockOpenOutlinedIcon/> </IconButton>
            </Tooltip>
        }
        managementButtons = <>
            {applicationStateButton}
            <Tooltip title="Close Application">
            <IconButton sx={{float: "right"}} size="small" color="error" onClick={() => {
                   makeApiCall({
                       url: `/api/careers/${props.id}/state`,
                       method: "PATCH",
                       body: {
                           state: "closed"
                       },
                       onLoadedCallback: () => {
                           navigate("/closed-careers")
                       },
                       onError: () => {}
                   })
               }}> <CancelOutlinedIcon/> </IconButton>
            </Tooltip>
        </>
    }

    if (props.state === "open") {
        if (props.blacklisted) {
            applicationBanner = <Alert severity="error">Blacklisted from Division - Click
                <Link underline={"none"} component={RouterLink} to={`/helpdesk/create/${divisionIdentifierUrlPrettifier(props.divisionIdentifier, true)}`}>
                    {" Here"}
                </Link> for Assistance</Alert>
        } else if (props.currentAssessment != null && meetsRequirements) {
            applicationBanner = <Alert severity="info">Continue with your application</Alert>
            applicationButton = <>
                <Button component={RouterLink}
                        to={`/assessments/user/${props.id}/take`} size={"small"} color="info"
                        variant="contained">Continue</Button>
                <Button color="error" size={"small"} sx={{ml:1}} variant="contained" onClick={() => {
                   setWithdrawConfirm(true)
                }}>Withdraw</Button>
            </>
        } else if (props.tooHighToApplyToRole && !props.canMark) {
            applicationBanner = <Alert severity="info">You already hold this {divisionIdentifierToDisplayName(props.type).toLowerCase()} or higher</Alert>
            applicationButton = <Button disabled sx={{width:"100%"}} size={"small"} color="error" variant="contained">Apply</Button>
        } else if (props.canMark) {
            applicationButton = <Button component={RouterLink}
                                        to={`/careers/overview/${props.id}/passed`} size={"small"} color="primary"
                                        variant="contained">Manage Applicants</Button>
        } else if (meetsRequirements) {
            applicationBanner = <Alert severity="success">You meet the requirements for this {divisionIdentifierToDisplayName(props.type).toLowerCase()}</Alert>
            applicationButton = <Button disabled={!meetsRequirements} sx={{ width:"100%" }} size={"small"} color="success" variant="contained" onClick={() => {
                setApplyConfirm(true)
            }}>Apply</Button>
        } else {
            if (!meetsRequirements && !props.tooHighToApplyToRole) {
                applicationBanner = <Alert severity="warning">You do not meet the requirements for this {divisionIdentifierToDisplayName(props.type).toLowerCase()}</Alert>
            }
            applicationButton = <Button disabled={!meetsRequirements} sx={{ width:"100%" }} size={"small"} color="success" variant="contained" onClick={() => {
                setApplyConfirm(true)
            }}>Apply</Button>
        }
    } else if (props.state === "locked" && user?.rank?.identifier !== "civilian") {
        applicationBanner = <Alert severity="error">Application Locked - Click
            <Link underline={"none"} component={RouterLink} to={`/helpdesk/create/${divisionIdentifierUrlPrettifier(props.divisionIdentifier, true)}`}>
                {" Here"}
            </Link> for Inquiries</Alert>
        if (props.canMark && props.state === "locked") {
            applicationButton = <Button component={RouterLink}
                                        to={`/careers/overview/${props.id}/passed`} size={"small"} color="primary"
                                        variant="contained">Manage Applicants</Button>
        }
    } else if (props.state === "locked" && user?.rank?.identifier === "civilian") {
        applicationBanner = <Alert severity="error">Application Locked</Alert>
    } else if (props.state === "editing") {
        applicationBanner = <Alert severity="info">Application Hidden</Alert>
        applicationButton = <Button component={RouterLink}
                                    to={`/edit-application/${props.id}`} size={"small"}  variant="contained" color="warning">Continue Editing Stages & Questions</Button>
    }

    if (props.lastAssessmentId != null) {
        lastAssessmentButton = <Button component={RouterLink}
                to={`/assessments/user/${props.lastAssessmentId}/review`} size={"small"} color="warning"
                variant="contained">Review last Stage</Button>
    }

    let applicationTitle
    if (props.type === "rank") {
        applicationTitle = props.rankOnPassDisplayName
    } else if (props.type === "division_role") {
        applicationTitle = props.roleName
    } else if (props.type === "taser") {
        applicationTitle = "Taser"
    } else if (props.type === "certification") {
        applicationTitle = props.certificationName
    } else {
        applicationTitle = "UNKNOWN APPLICATION TYPE"
    }

    return (
        <Card sx={{ maxWidth: 500 }}>
        {applicationBanner}
        <Stack sx={{float: "right", mt: 0.5, mr: 1, overflow: "hidden"}} direction={"row"}>
            <Tooltip title="Edit Application Requirements">
            {editApplicationRequirementsButton}
            </Tooltip>
            {managementButtons}
            </Stack>
            <CardHeader
                title = <Typography variant="h6">{applicationTitle}</Typography>
                subheader = {props.divisionName}
                avatar = {<Avatar
                    src={props.divisionBadge}
                    sx={{ width: 85, height: 85}}
                    variant="square"
                />}/>
            <CardContent>
                <Typography sx={{ mt: -2.5}} variant="body2" style={{whiteSpace: 'pre-line'}}>
                    {props.description}
                </Typography>
                <hr/>
                {
                    props.requirements?.map(requirement => {
                        return <ApplicationRequirement type={requirement.type} param={requirement.param} met={requirement.met} key={requirement.id}/>
                    })
                }
                <Stack spacing={2} sx={{mt: 1}}>
                    {applicationButton}
                    {lastAssessmentButton}
                </Stack>
            </CardContent>
            <ConfirmationDialog title={"Apply"} description={"Opening a application will indicate your interest for the role, however withdrawing your application will incur a cooldown"} open={applyConfirm} onClose={apply} />
            <ConfirmationDialog title={"Withdraw"} description={"Withdrawing your application will incur a cooldown, however if you re-apply you will need to start from scratch"} open={withdrawConfirm} onClose={withdraw} />
        </Card>
    )
}

export const CareersPage = () => {
    let accessibleDivisions: Division[] = []
    const applications = useApiCall<Array<CareersResponse>>({url: `/api/careers/`})
    const divisions = useApiCall<Division[]>({url: `/api/division/list-divisions`})
    const userData = useUser()
    const user = userData.user

    let newCareerButton = <></>
    divisions.data?.forEach(division => {
        if (userData.hasPermissions(`${division.identifier}-careers:create`)) {
            accessibleDivisions = [...accessibleDivisions, division]
        }
    })
    if (accessibleDivisions.length > 0) {
        newCareerButton = <>
        <Stack spacing={2} direction="row">
            <NewApplicationDivisionSelectionForm divisions={accessibleDivisions} />
            <Button sx={{width: 200}} color="info" variant="contained" component={RouterLink}
                    to={`/closed-careers`}>Closed Applications</Button>
        </Stack>
        </>
    }

    let pageContent
    if (user?.blacklisted && user?.rank?.identifier === "civilian") {
        pageContent = <Stack spacing={2}>
            <Alert severity="error">You are blacklisted from the Paralake Police Department's Application Portal</Alert>
        </Stack>
    } else if (user?.suspended) {
        pageContent = <Stack spacing={2}>
            <Alert severity="error">You are blacklisted from the Paralake Police Department's Application Portal</Alert>
            <Alert severity="warning">This blacklist is temporary due to being suspended, this will be removed by Internal Affairs Command when the complaint against you is reviewed</Alert>
        </Stack>
    } else if (user?.blacklisted && user?.rank?.identifier === "probationary_officer") {
            pageContent = <Stack spacing={2}>
                <Alert severity="error">You are blacklisted from the Paralake Police Department's Application Portal</Alert>
                <Alert severity="warning">You are a Probationary Officer so your application blacklist will be lifted when you pass your Probationary Officer review</Alert>
            </Stack>
    } else {
        pageContent = <Stack spacing={2}>
            {newCareerButton}
            <Grid container spacing={{ xs: 2, md: 3 }} columns={{ xs: 4, sm: 8, md: 12 }}>
                {applications.data && applications.data.map(application => {
                        return <Grid sx={{paddingTop: 5, paddingRight: 3}} xs={3} key={application.id}>
                            <Application
                                id={application.id}
                                state={application.state}
                                divisionName={application.divisionName}
                                divisionIdentifier={application.divisionIdentifier}
                                divisionBadge={application.divisionBadge}
                                certificationName={application.certificationName}
                                roleName={application.roleName}
                                roleIdentifier={application.roleIdentifier}
                                description={application.description}
                                requirements={application.requirements}
                                currentAssessment={application.currentAssessment}
                                lastAssessmentId={application.lastAssessmentId}
                                tooHighToApplyToRole={application.tooHighToApplyToRole}
                                canMark={application.canMark}
                                canEdit={application.canEdit}
                                rankOnPassDisplayName={application.rankOnPassDisplayName}
                                type={application.type}
                                blacklisted={application.blacklisted}
                            />
                        </Grid>
                    }
                )}
            </Grid>
        </Stack>
    }

    return (
        <LoadingContent isLoading={
            userData.isLoading || !user ||
            applications.isLoading ||  !applications ||
            divisions.isLoading || !divisions
        }>
            {pageContent}
        </LoadingContent>
    )
}